/* Recently viewed (sections/recently-viewed.liquid). The product page keeps the handles of the pieces a shopper
   opened in this browser (localStorage 'lmb:recent': newest first, no repeats, at most 12; written by
   assets/product.js). This reads that list, leaves out the piece on screen, and when the section comes within about
   400 px of the screen fetches each piece's grid card from /products/<handle>?section_id=recent-card, six at a time,
   until the section's limit is reached; the section shows as soon as the first card arrives. With no list (a first
   visit) nothing is fetched and the section stays hidden.
   Pieces that no longer exist are dropped from the list; a piece whose handle changed keeps its place under the new
   one; pieces that fail to load (offline, server error) are just skipped this time.
   "Clear" empties the list and replaces the cards with a short line that takes focus, so the result is read out.
   Every storage access is wrapped: private windows and blocked storage simply show nothing. */
(() => {
  if (customElements.get('lmb-recently-viewed')) return;

  const KEY = 'lmb:recent';
  const MAX = 12;
  const BATCH = 6;

  function read() {
    try {
      const list = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list.filter((handle) => typeof handle === 'string' && handle) : [];
    } catch (error) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
    } catch (error) {
      /* Storage full or blocked: the list just isn't tidied this time. */
    }
  }

  function forget() {
    try {
      localStorage.removeItem(KEY);
    } catch (error) {
      /* Blocked storage: there was nothing to show anyway. */
    }
  }

  customElements.define(
    'lmb-recently-viewed',
    class LmbRecentlyViewed extends HTMLElement {
      connectedCallback() {
        this.inner = this.querySelector('[data-recent-inner]');
        this.list = this.querySelector('[data-rail-track]');
        this.done = this.querySelector('[data-recent-done]');
        this.clearButton = this.querySelector('[data-recent-clear]');
        this.limit = Number(this.dataset.limit) || 8;
        this.onClear = () => this.clear();
        if (this.clearButton) this.clearButton.addEventListener('click', this.onClear);

        const current = this.dataset.current || '';
        this.handles = read().filter((handle) => handle !== current);
        if (!this.handles.length || !this.list) return;

        if (!('IntersectionObserver' in window)) {
          this.load();
          return;
        }
        // The element keeps its place in the layout (zero height while hidden), so it can be watched.
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            this.load();
          },
          { rootMargin: '400px 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.clearButton) this.clearButton.removeEventListener('click', this.onClear);
      }

      // Resolves to a card <li>, 'gone' (deleted or unpublished) or 'failed' (try again another time).
      async fetchCard(handle) {
        try {
          const response = await fetch(`${this.dataset.cardUrl}${encodeURIComponent(handle)}?section_id=recent-card`, {
            credentials: 'same-origin',
          });
          if (response.status === 404) return 'gone';
          if (!response.ok) return 'failed';
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const cell = doc.querySelector('[data-recent-cell]');
          if (cell) return cell;
          return doc.querySelector('[data-recent-missing]') ? 'gone' : 'failed';
        } catch (error) {
          return 'failed';
        }
      }

      async load() {
        const current = this.dataset.current || '';
        const queue = this.handles.slice();
        const shown = new Set();
        const gone = [];
        const renamed = new Map();
        while (queue.length && shown.size < this.limit && !this.cleared) {
          const batch = queue.splice(0, Math.min(BATCH, this.limit - shown.size));
          const results = await Promise.all(batch.map((handle) => this.fetchCard(handle)));
          if (this.cleared) return;
          results.forEach((result, i) => {
            const handle = batch[i];
            if (result === 'gone') {
              gone.push(handle);
              return;
            }
            if (result === 'failed') return;
            const actual = result.dataset.recentCell;
            if (actual !== handle) renamed.set(handle, actual);
            if (shown.has(actual) || actual === current || shown.size >= this.limit) return;
            if (!shown.size) this.list.querySelectorAll('[data-rail-placeholder]').forEach((cell) => cell.remove());
            shown.add(actual);
            this.list.append(result);
          });
          if (shown.size) this.show();
        }
        this.tidy(gone, renamed);
      }

      show() {
        this.list.removeAttribute('aria-busy');
        this.inner.hidden = false;
      }

      /* Drops gone pieces and renames moved ones in the stored list (read again: another tab may have changed it). */
      tidy(gone, renamed) {
        if (this.cleared || (!gone.length && !renamed.size)) return;
        const seen = new Set();
        const list = [];
        read().forEach((handle) => {
          if (gone.includes(handle)) return;
          const actual = renamed.get(handle) || handle;
          if (seen.has(actual)) return;
          seen.add(actual);
          list.push(actual);
        });
        write(list);
      }

      clear() {
        this.cleared = true;
        if (this.observer) this.observer.disconnect();
        forget();
        this.list.replaceChildren();
        this.list.hidden = true;
        if (this.clearButton) this.clearButton.hidden = true;
        this.inner.hidden = false;
        if (this.done) {
          this.done.hidden = false;
          this.done.focus();
        }
      }
    }
  );
})();
